import { useCustomerSubscriptionAdminFindCalculateCount } from '@/api'
import { KBSuspenseLoading } from '@/components/atoms/KBSuspenseLoading'
import KBUpgrade, {
  KBUpgradeItemType,
} from '@/components/molecules/KBUpgrade/KBUpgrade'
import { isPLGPaidUserAtom } from '@/store/pricing.atom'
import { isTrialAtom } from '@/store/trial.atom'
import dayjs from '@wox/dayjs'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'

interface CheckSubscriptionProps {
  type: 'resources' | 'tasks' | 'deliveries'
}

/**
 * @description 检查资源订阅情况 - 订阅可用则返回子组件，不可用返回缺省页
 * Trial users and PLG paid users (STARTER/PREMIUM) get full access without subscription check
 */
const CheckResourceSubscriptionLayout = ({ type }: CheckSubscriptionProps) => {
  const { t } = useTranslation()
  const isTrial = useAtomValue(isTrialAtom)
  const isPLGPaid = useAtomValue(isPLGPaidUserAtom)

  const { data: purchaseList, isLoading: purchaseLoading } =
    useCustomerSubscriptionAdminFindCalculateCount()

  if (purchaseLoading) {
    return <KBSuspenseLoading />
  }

  // Trial users and PLG paid users get full access - bypass subscription check
  // PLG users (STARTER/PREMIUM) are confirmed paid via pricing_tier, no CustomerSubscription needed
  if (isTrial || isPLGPaid) {
    return <Outlet />
  }

  // 未订阅
  const notSubscribed = [
    purchaseList?.resource_exclusive,
    purchaseList?.resource_shared,
  ].every(
    (i) => i?.subscribed_count === 0
    // (i) => i?.subscribed_count < 10000
  )
  // 超过使用期
  const isExpired =
    dayjs().isAfter(purchaseList?.resource_exclusive?.renewal_date) &&
    dayjs().isAfter(purchaseList?.resource_shared?.renewal_date)

  const allTips = {
    resources: {
      title: t('resource.resourceManagement'),
      subtitle: t('common.resourceNotSubscribedTitle'),
      description: t('common.resourceNotSubscribedDescription'),
      items: [
        {
          icon: 'leaf',
          itemTile: t('common.resourceItemOneTitle'),
          itemDescription: t('common.resourceItemOneDescription'),
        },
        {
          icon: 'handshake',
          itemTile: t('common.resourceItemTwoTitle'),
          itemDescription: t('common.resourceItemTwoDescription'),
        },
        {
          icon: 'users',
          itemTile: t('common.resourceItemThreeTitle'),
          itemDescription: t('common.resourceItemThreeDescription'),
        },
      ],
    },
    tasks: {
      title: t('common.task'),
      subtitle: t('common.taskNotSubscribedTitle'),
      description: t('common.taskNotSubscribedDescription'),
      items: [
        {
          icon: 'leaf',
          itemTile: t('common.taskItemOneTitle'),
          itemDescription: t('common.taskItemOneDescription'),
        },
        {
          icon: 'handshake',
          itemTile: t('common.taskItemTwoTitle'),
          itemDescription: t('common.taskItemTwoDescription'),
        },
        {
          icon: 'users',
          itemTile: t('common.taskItemThreeTitle'),
          itemDescription: t('common.taskItemThreeDescription'),
        },
      ],
    },
    deliveries: {
      title: t('common.delivery'),
      subtitle: t('common.deliveryNotSubscribedTitle'),
      description: t('common.deliveryNotSubscribedDescription'),
      items: [
        {
          icon: 'leaf',
          itemTile: t('common.deliveryItemOneTitle'),
          itemDescription: t('common.deliveryItemOneDescription'),
        },
        {
          icon: 'handshake',
          itemTile: t('common.deliveryItemTwoTitle'),
          itemDescription: t('common.deliveryItemTwoDescription'),
        },
        {
          icon: 'users',
          itemTile: t('common.deliveryItemThreeTitle'),
          itemDescription: t('common.deliveryItemThreeDescription'),
        },
      ],
    },
  }

  if (notSubscribed || isExpired) {
    return (
      <KBUpgrade
        title={allTips[type].title}
        subtitle={allTips[type].subtitle}
        description={allTips[type].description}
        items={allTips[type].items as KBUpgradeItemType[]}
      />
    )
  }

  return <Outlet />
}

export default CheckResourceSubscriptionLayout
